import { useCallback, useEffect, useRef, useState } from 'react';
import { useAxiosSwagger } from './useAxiosSwagger';
import { describeError } from '../api/requeue';
import {
    ExactConnection,
    ExactConnectionState,
    ExactRateLimit,
    getExactConnection,
    getExactRateLimit,
    refreshExactConnection,
} from '../api/exactConnection';

/** How often we check whether Exact has updated the tokens yet. */
const POLL_INTERVAL_MS = 3000;

/**
 * How long we keep checking. Signing in at Exact rarely takes more than a minute, but with
 * a forgotten tab this counter would run forever.
 */
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export interface ExactConnectionMonitor {
    connection: ExactConnection | null;
    /**
     * How much of the daily Exact API budget is left, or null while nothing has been read.
     *
     * Loaded alongside the connection rather than by a hook of its own: same panel, same
     * environment, same refresh button, and two hooks would mean two loading flags for one
     * section. It does fail on its own - an unreadable log table leaves this null and
     * leaves the connection exactly as it was.
     */
    rateLimit: ExactRateLimit | null;
    loading: boolean;
    error: string | null;
    /** Refreshing through the refresh token that is already there. */
    refreshing: boolean;
    /** An Exact login was opened and we are waiting for the tokens to be updated. */
    awaitingLogin: boolean;
    reload: () => void;
    refresh: () => void;
    reconnect: () => void;
    stopWaiting: () => void;
}

/**
 * Tracks the Exact connection of a single customer environment.
 *
 * Signing in at Exact stays a human job; that is exactly what OAuth is for. Everything
 * around it does not: this hook fetches the ready-made authorization URL, opens it, and
 * then checks for the tokens itself. So you no longer have to guess whether it worked.
 */
export const useExactConnection = (
    customer?: string,
    environment?: string
): ExactConnectionMonitor => {
    const axios = useAxiosSwagger();

    const [connection, setConnection] = useState<ExactConnection | null>(null);
    const [rateLimit, setRateLimit] = useState<ExactRateLimit | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [awaitingLogin, setAwaitingLogin] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    /** The state from just before opening the login, to recognise a change. */
    const baseline = useRef<{
        reconnectedAt: string | null;
        state: ExactConnectionState;
    } | null>(null);

    const mounted = useRef<boolean>(true);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    const load = useCallback(async (): Promise<ExactConnection | null> => {
        if (!axios || !customer || !environment) {
            return null;
        }

        try {
            const result = await getExactConnection(
                axios,
                customer,
                environment
            );

            if (mounted.current) {
                setConnection(result);
                setError(result.error ?? null);
            }

            return result;
        } catch (e) {
            if (mounted.current) {
                setError(describeError(e));
            }

            return null;
        }
    }, [axios, customer, environment]);

    /**
     * The budget reading, fetched apart and allowed to fail apart.
     *
     * Its own call because it costs its own thing - the connection is a settings read, this
     * searches a log partition - and its own catch because a log table nobody can read must
     * not turn the connection panel into an error. A failure here leaves the block off the
     * screen, which is the same as an environment that has never called Exact.
     */
    const loadRateLimit = useCallback(async (): Promise<void> => {
        if (!axios || !customer || !environment) {
            return;
        }

        try {
            const result = await getExactRateLimit(axios, customer, environment);

            if (mounted.current) {
                setRateLimit(result);
            }
        } catch {
            if (mounted.current) {
                setRateLimit(null);
            }
        }
    }, [axios, customer, environment]);

    const reload = useCallback(async (): Promise<ExactConnection | null> => {
        const result = await load();

        void loadRateLimit();

        return result;
    }, [load, loadRateLimit]);

    useEffect(() => {
        setConnection(null);
        setRateLimit(null);
        setAwaitingLogin(false);
        setError(null);
        baseline.current = null;

        if (!axios || !customer || !environment) {
            return;
        }

        setLoading(true);

        load().finally(() => {
            if (mounted.current) {
                setLoading(false);
            }
        });

        void loadRateLimit();
    }, [axios, customer, environment, load, loadRateLimit]);

    useEffect(() => {
        if (!awaitingLogin) {
            return;
        }

        const startedAt = Date.now();

        const timer = setInterval(async () => {
            const result = await load();

            if (!result) {
                return;
            }

            const before = baseline.current;

            // The manual-connect timestamp only changes because of this round, so it is the
            // most reliable sign that it worked. An environment going from expired to
            // connected counts as well, in case that timestamp is not set.
            const reconnected =
                (before?.reconnectedAt ?? null) !==
                    (result.reconnectedAt ?? null) ||
                (before?.state !== 'Connected' && result.state === 'Connected');

            if (reconnected || Date.now() - startedAt > POLL_TIMEOUT_MS) {
                if (mounted.current) {
                    setAwaitingLogin(false);
                }
            }
        }, POLL_INTERVAL_MS);

        return () => clearInterval(timer);
    }, [awaitingLogin, load]);

    const refresh = useCallback(async () => {
        if (!axios || !customer || !environment) {
            return;
        }

        setRefreshing(true);
        setError(null);

        try {
            const result = await refreshExactConnection(
                axios,
                customer,
                environment
            );

            if (mounted.current) {
                setConnection(result);
                setError(result.error ?? null);
            }
        } catch (e) {
            if (mounted.current) {
                setError(describeError(e));
            }
        } finally {
            if (mounted.current) {
                setRefreshing(false);
            }
        }
    }, [axios, customer, environment]);

    const reconnect = useCallback(() => {
        if (!connection?.authorizeUrl) {
            return;
        }

        baseline.current = {
            reconnectedAt: connection.reconnectedAt ?? null,
            state: connection.state,
        };

        setAwaitingLogin(true);

        window.open(connection.authorizeUrl, '_blank', 'noopener,noreferrer');
    }, [connection]);

    const stopWaiting = useCallback(() => setAwaitingLogin(false), []);

    return {
        connection,
        rateLimit,
        loading,
        error,
        refreshing,
        awaitingLogin,
        reload,
        refresh,
        reconnect,
        stopWaiting,
    };
};
